import { faRotateLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, ConfirmationModal, Flex, Text, TextButton } from '@modusign/design-system';
import { useUpdateDocumentSigners } from '@modusign/openapi-client-external-document';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form-v7';
import { useToggle } from 'react-use';
import { v4 } from 'uuid';

import noop from '@libs/noop';

import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';

import { ExternalDocument } from '../../../../types/ExternalDocument';
import ParticipantsEditor, { SignerFormState } from '../../../ParticipantsEditor';

interface ParticipantsEditorProps {
  externalDocument: ExternalDocument;
  onIsDirtyChange: (isDirty: boolean) => void;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function Editor({
  externalDocument,
  onCancel,
  onSuccess,
  onIsDirtyChange,
}: ParticipantsEditorProps) {
  const workspaceId = useWorkspaceId();
  const { createErrorHandler } = useOpenErrorModal();

  const normalizedInitParticipants = useMemo(() => {
    const ids: string[] = [];
    const map: Record<string, SignerFormState> = {};

    externalDocument.signers.forEach((p) => {
      const id = v4();
      ids.push(id);
      map[id] = {
        contact: { email: p.email || undefined, phone: p.phone || undefined },
        externalSigner: p.externalSigner,
        name: p.name,
        type: p.type,
        company: p.company,
        personInCharge: p.personInCharge,
      };
    });

    return { ids, map };
  }, [externalDocument.signers]);

  const { mutateAsync } = useUpdateDocumentSigners({
    mutation: {
      onError: createErrorHandler({ title: '외부 문서 수정 실패' }),
      onSuccess,
    },
  });

  const formMethods = useForm<{ participants: Record<string, SignerFormState> }>({
    mode: 'onChange',
    defaultValues: {
      participants: normalizedInitParticipants.map,
    },
  });
  const [participantIds, setParticipantIds] = useState<string[]>(
    normalizedInitParticipants.ids.length ? normalizedInitParticipants.ids : [v4()]
  );
  const [isResetModalOpen, toggleResetModal] = useToggle(false);

  const {
    formState: { isDirty, isSubmitting },
    handleSubmit,
    trigger,
  } = formMethods;

  useEffect(() => {
    onIsDirtyChange(isDirty);
  }, [isDirty, onIsDirtyChange]);

  const submitHandler = handleSubmit(
    (data) =>
      mutateAsync({
        workspaceId,
        externalDocumentId: externalDocument.id,
        data: {
          signers: participantIds.map((id) => {
            const participant = data.participants[id];
            const personInCharge =
              participant.personInCharge?.name ||
              participant.personInCharge?.email ||
              participant.personInCharge?.phone
                ? {
                    name: participant.personInCharge?.name || null,
                    email: participant.personInCharge?.email || null,
                    phone: participant.personInCharge?.phone || null,
                  }
                : null;

            return {
              type: participant.type || 'INDIVIDUAL',
              externalSigner: participant.externalSigner,
              name: participant.name,
              phone: participant.contact?.phone || null,
              email: participant.contact?.email || null,
              personInCharge,
              company: participant.company || null,
            };
          }),
        },
        // 별도 오류처리를 해서 mutateAsync의 reject를 무시합니다.
      }).catch(noop),
    () => trigger()
  );

  return (
    <FormProvider {...formMethods}>
      <Box
        as="form"
        onReset={(e: FormEvent) => {
          e.preventDefault();
          toggleResetModal();
        }}
        onSubmit={submitHandler}
      >
        <ConfirmationModal
          confirmButtonLabel="초기화하기"
          isOpen={isResetModalOpen}
          maxWidth="xxs"
          title="정보 초기화"
          onClose={toggleResetModal}
          onConfirm={() => {
            setParticipantIds([v4()]);
            toggleResetModal();
          }}
        >
          <Text textAlign="center">입력한 내용을 모두 삭제할까요?</Text>
        </ConfirmationModal>
        <ParticipantsEditor
          getPersonInCharge={() => externalDocument.personInCharge}
          participantIds={participantIds}
          setParticipantIds={setParticipantIds}
        />
        <Flex
          alignItems="center"
          flexDirection="row-reverse"
          justifyContent="space-between"
          mb={[10, null, 0]}
          mt={9}
        >
          <div>
            <Button disabled={isSubmitting} type="button" onClick={onCancel}>
              취소
            </Button>
            <Button isLoading={isSubmitting} ml={3} type="submit" variant="primary">
              저장하기
            </Button>
          </div>
          <TextButton disabled={isSubmitting} type="reset">
            <FontAwesomeIcon icon={faRotateLeft} /> 초기화
          </TextButton>
        </Flex>
      </Box>
    </FormProvider>
  );
}
